import React, { useEffect, useRef, useState } from 'react';
import { 
  FaUser, FaEnvelope, FaPhone, FaBookOpen, FaCalendarAlt,
  FaCheckCircle, FaArrowRight, FaStar, FaPaperPlane,
  FaGraduationCap, FaMapMarkerAlt, FaVenusMars, FaBirthdayCake,
  FaFileAlt, FaUpload, FaRupeeSign, FaRegClock
} from 'react-icons/fa';
import { MdLocationOn, MdMessage, MdPayment } from 'react-icons/md';

const RegistrationSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    fatherName: '',
    motherName: '',
    email: '',
    phone: '',
    dob: '',
    gender: '',
    address: '',
    course: '',
    qualification: '',
    message: ''
  });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({
        fullName: '',
        fatherName: '',
        motherName: '',
        email: '',
        phone: '',
        dob: '',
        gender: '',
        address: '',
        course: '',
        qualification: '',
        message: ''
      });
    }, 3000);
  };

  const courses = [
    { value: "", label: "Select Course" },
    { value: "bcom", label: "Bachelor of Commerce (B.Com)" },
    { value: "bsc", label: "Bachelor of Science (B.Sc)" },
    { value: "ba", label: "Bachelor of Arts (B.A)" }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-8 h-1 bg-blue-600 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-700 rounded-full"></div>
          </div>

          {/* Heading */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 rounded-2xl shadow-lg">
                <FaFileAlt className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-blue-700 to-indigo-800 bg-clip-text text-transparent">
                Student Registration
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4">
              Register now for academic session 2024-25
            </p>
          </div>

          {/* Registration Form */}
          <div className={`bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            
            {/* Form Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5">
              <h3 className="text-white font-bold text-xl text-center">Registration Form</h3>
              <p className="text-white/90 text-sm text-center mt-1">Please fill all required details carefully</p>
            </div>

            <div className="p-6 md:p-8">
              {formSubmitted ? (
                <div className="text-center py-8">
                  <FaCheckCircle className="text-green-500 text-5xl mx-auto mb-3" />
                  <p className="text-gray-800 font-semibold text-lg">Registration Successful!</p>
                  <p className="text-gray-500 text-sm mt-2">We'll contact you soon with further details.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Personal Information Section */}
                  <div>
                    <h4 className="text-blue-700 font-bold text-md mb-3 flex items-center gap-2 border-b border-blue-200 pb-2">
                      <FaUser className="text-blue-500" />
                      Personal Information
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Father's Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="fatherName"
                          value={formData.fatherName}
                          onChange={handleChange}
                          placeholder="Enter father's name"
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Mother's Name
                        </label>
                        <input
                          type="text"
                          name="motherName"
                          value={formData.motherName}
                          onChange={handleChange}
                          placeholder="Enter mother's name"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="dob"
                          value={formData.dob}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Gender <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 bg-white"
                        >
                          <option value="">Select Gender</option>
                          <option value="female">Female</option>
                          <option value="male">Male</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h4 className="text-blue-700 font-bold text-md mb-3 flex items-center gap-2 border-b border-blue-200 pb-2">
                      <FaEnvelope className="text-blue-500" />
                      Contact Information
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Mobile Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 1234567890"
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Address <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          rows="2"
                          placeholder="Enter your complete address"
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all resize-none"
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  {/* Academic Information */}
                  <div>
                    <h4 className="text-blue-700 font-bold text-md mb-3 flex items-center gap-2 border-b border-blue-200 pb-2">
                      <FaGraduationCap className="text-blue-500" />
                      Academic Information
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Interested Course <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="course"
                          value={formData.course}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 bg-white"
                        >
                          {courses.map((course, idx) => (
                            <option key={idx} value={course.value}>{course.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          Highest Qualification <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="qualification"
                          value={formData.qualification}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 bg-white"
                        >
                          <option value="">Select Qualification</option>
                          <option value="10th">10th Pass</option>
                          <option value="12th">12th Pass</option>
                          <option value="graduate">Graduate</option>
                          <option value="postgraduate">Post Graduate</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <h4 className="text-blue-700 font-bold text-md mb-3 flex items-center gap-2 border-b border-blue-200 pb-2">
                      <MdMessage className="text-blue-500" />
                      Additional Information
                    </h4>
                    <div>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Any additional information or message (optional)"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all resize-none"
                      ></textarea>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    Register Now
                    <FaPaperPlane className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </button>

                  <p className="text-gray-400 text-xs text-center flex items-center justify-center gap-1 mt-3">
                    <FaRegClock className="text-xs" />
                    Registration closes on 30th June 2024
                  </p>
                </form>
              )}
            </div>
          </div>

          {/* Info Cards */}
          <div className={`grid md:grid-cols-3 gap-5 mt-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '100ms' }}>
            
            <div className="bg-white rounded-xl p-4 text-center shadow-md hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <FaCalendarAlt className="text-blue-600 text-xl" />
              </div>
              <p className="text-gray-800 font-semibold text-sm">Last Date to Apply</p>
              <p className="text-gray-500 text-xs">30th June 2024</p>
            </div>

            <div className="bg-white rounded-xl p-4 text-center shadow-md hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <FaRupeeSign className="text-blue-600 text-xl" />
              </div>
              <p className="text-gray-800 font-semibold text-sm">Registration Fee</p>
              <p className="text-gray-500 text-xs">₹ 500 (Non-refundable)</p>
            </div>

            <div className="bg-white rounded-xl p-4 text-center shadow-md hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <FaCheckCircle className="text-blue-600 text-xl" />
              </div>
              <p className="text-gray-800 font-semibold text-sm">Documents Required</p>
              <p className="text-gray-500 text-xs">10th, 12th Marksheets</p>
            </div>
          </div>

          {/* Help Section */}
          <div className={`mt-8 text-center transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '200ms' }}>
            <p className="text-gray-500 text-sm">Need help with registration?</p>
            <p className="text-blue-600 text-sm font-semibold">Call us: +91 7080 100 333 | Email: admission@bnsmahila.edu</p>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default RegistrationSection;